"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import type { FUSportsLivescoreEvent, SearchEntityResult } from "fansunited-data-layer";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { asCompetition } from "@/views/livescore/utils/livescore";
import type { LivescoreSport } from "@/views/livescore/utils/livescore";
import type { MatchOddsMap } from "@/features/odds/types";
import type { ResolvedListings } from "@/features/tv-listings/utils/resolveMatchListings";
import { CompactMatchRow } from "./CompactMatchRow";

/** Competition pages (standings, overview, etc.) exist only for football today. */
const SPORTS_WITH_COMPETITION_PAGE = new Set<LivescoreSport>(["football"]);
interface Props {
  competitionId: string;
  events: FUSportsLivescoreEvent[];
  entities: Record<string, SearchEntityResult>;
  sport: LivescoreSport;
  oddsMap: MatchOddsMap;
  showOdds?: boolean;
  /** Optional label appended to the competition name (e.g. "Matchday 12", "Quarter-Final"). */
  roundLabel?: string | null;
  /** Map of match ID → pre-resolved broadcaster listings (parent batch-fetches). */
  tvByMatch?: Record<string, ResolvedListings>;
  /** When true, every row reserves the TV icon slot for alignment. */
  reserveTvSlot?: boolean;
}

/**
 * Competition-grouped, vertically-stacked match list for narrow contexts.
 *
 * Header layout:
 *   [ LOGO ]  Competition Name · Round Label
 *              Country
 */
export function CompactMatchGroup(t0) {
  const $ = _c(51);
  const {
    competitionId,
    events,
    entities,
    sport,
    oddsMap,
    showOdds,
    roundLabel,
    tvByMatch,
    reserveTvSlot: t1
  } = t0;
  const reserveTvSlot = t1 === undefined ? false : t1;
  const nav = useNavigation();
  let t2;
  if ($[0] !== competitionId || $[1] !== entities) {
    t2 = asCompetition(entities, competitionId);
    $[0] = competitionId;
    $[1] = entities;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const competition = t2;
  const competitionName = competition?.name ?? competitionId;
  let t3;
  if ($[3] !== sport) {
    t3 = SPORTS_WITH_COMPETITION_PAGE.has(sport);
    $[3] = sport;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const hasCompetitionPage = t3;
  let t4;
  if ($[5] !== competitionId || $[6] !== hasCompetitionPage || $[7] !== nav) {
    t4 = hasCompetitionPage ? nav.standings(competitionId) : undefined;
    $[5] = competitionId;
    $[6] = hasCompetitionPage;
    $[7] = nav;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const standingsHref = t4;
  let t5;
  if ($[9] !== competitionName || $[10] !== standingsHref) {
    t5 = standingsHref ? <Link href={standingsHref} prefetch={false} className="text-sm font-semibold truncate link-neutral hover:underline">{competitionName}</Link> : <span className="text-sm font-semibold truncate">{competitionName}</span>;
    $[9] = competitionName;
    $[10] = standingsHref;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const compNameContent = t5;
  const t6 = competition?.assets?.logo;
  let t7;
  if ($[12] !== competitionId || $[13] !== competitionName || $[14] !== t6) {
    t7 = <TeamLogo src={t6} alt={competitionName} size="md" kind="competition" competitionId={competitionId} />;
    $[12] = competitionId;
    $[13] = competitionName;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== compNameContent) {
    t8 = <div className="min-w-0 truncate">{compNameContent}</div>;
    $[16] = compNameContent;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== roundLabel) {
    t9 = roundLabel && <><span className="text-muted-foreground/60 shrink-0">·</span><span className="text-sm text-muted-foreground truncate shrink">{roundLabel}</span></>;
    $[18] = roundLabel;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <div className="flex items-baseline gap-2 min-w-0">{t8}{t9}</div>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== competition) {
    t11 = competition?.country?.name && <span className="text-xs text-muted-foreground truncate mt-0.5">{competition.country.name}</span>;
    $[23] = competition;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <div className="flex-1 min-w-0 flex flex-col leading-tight">{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t7) {
    t13 = <div className="flex items-center gap-3 px-3 py-2.5 border-b border-border">{t7}{t12}</div>;
    $[28] = t12;
    $[29] = t7;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== entities || $[32] !== events || $[33] !== oddsMap || $[34] !== reserveTvSlot || $[35] !== showOdds || $[36] !== sport || $[37] !== tvByMatch) {
    let t15;
    if ($[39] !== entities || $[40] !== oddsMap || $[41] !== reserveTvSlot || $[42] !== showOdds || $[43] !== sport || $[44] !== tvByMatch) {
      t15 = event => <CompactMatchRow key={event.id} event={event} entities={entities} sport={sport} odds={oddsMap.get(event.id)?.[0]} showOdds={showOdds} tvListings={tvByMatch?.[event.id]} reserveTvSlot={reserveTvSlot} />;
      $[39] = entities;
      $[40] = oddsMap;
      $[41] = reserveTvSlot;
      $[42] = showOdds;
      $[43] = sport;
      $[44] = tvByMatch;
      $[45] = t15;
    } else {
      t15 = $[45];
    }
    t14 = events.map(t15);
    $[31] = entities;
    $[32] = events;
    $[33] = oddsMap;
    $[34] = reserveTvSlot;
    $[35] = showOdds;
    $[36] = sport;
    $[37] = tvByMatch;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[46] !== t14) {
    t15 = <div className="divide-y divide-border/50">{t14}</div>;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t13 || $[49] !== t15) {
    t16 = <div className="mb-3 bg-card rounded-md border border-border overflow-hidden">{t13}{t15}</div>;
    $[48] = t13;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  return t16;
}